import React, { FC, useState } from 'react'
import styled from 'styled-components'
import { Box, Flex } from '@/components/atoms/Grid'
import { Body, H2 } from '@/components/atoms/typography'
import FormElement from '@/components/molecules/form/formElement'
import FormInput, { FormInputType } from '@/components/molecules/form/formInput'
import { newEmailValidator } from '@/core/validationUtils'
import { Sdk } from '@/network/graphql.g'
import { validFor } from '@/theme/validFor'
import useGraphQl from 'hooks/useGraphQl'
import { useMhubIdentifyByEmail } from 'hooks/useMhubIdentifyByEmail'
import useTranslation from 'next-translate/useTranslation'
import { transparentize } from 'polished'

const BlogNewsletterForm: FC = () => {
  const { t } = useTranslation('common')
  const { requestNotifySuccessError } = useGraphQl()
  const identifyByEmail = useMhubIdentifyByEmail()
  const [email, setEmail] = useState<string>('')
  const [showValidation, setShowValidation] = useState<boolean>(false)
  const errorMessage = t('UserDataForm.emailErrorPlaceholder')
  const isValid = newEmailValidator(email)

  const handleInputChange = (name: string, value: string) => {
    setEmail(value)
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setShowValidation(true)
    if (isValid) {
      const result = await requestNotifySuccessError((sdk: Sdk) => ({
        method: sdk.newsletterEmail,
        variables: {
          email: email
        },
        successText: () => t('Account.Newsletter.successSubscribe')
      }))
      if (result.success) {
        identifyByEmail({ email })
        setShowValidation(false)
        setEmail('')
      }
    }
  }

  return (
    <NewsletterContainer justifyContent="center" mb={3}>
      <StyledFlex>
        <StyledH2>{t('Blog.Newsletter.newsletterTitle')}</StyledH2>
        <StyledBody>{t('Blog.Newsletter.newsletterSubtitle')}</StyledBody>
        <StyledForm onSubmit={handleSubmit}>
          <StyledGrid width="100%" m={0}>
            <FormElement showValidation={showValidation} isValid={isValid}>
              <StyledFormInput
                name="email"
                type="email"
                value={email}
                adjustPlaceholderPosition
                formInputType={FormInputType.Text}
                placeholder={t('Blog.Newsletter.newsletterPlaceholder')}
                isError={showValidation && !isValid}
                errorPlaceholder={errorMessage}
                onChange={handleInputChange}
              />
            </FormElement>
            <StyledButton type="submit">
              {t('Blog.Newsletter.newsletterButton')}
            </StyledButton>
          </StyledGrid>
        </StyledForm>
      </StyledFlex>
    </NewsletterContainer>
  )
}

export default BlogNewsletterForm

const NewsletterContainer = styled(Flex)`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-top: 8px solid ${({ theme }) => theme.colors.border};
  margin-top: 30px;
`

const StyledFlex = styled(Flex)`
  flex-direction: column;
  align-items: center;
  padding: 50px;
  max-width: 550px;
  ${validFor.mobile` 
    width: 100%;
    padding: 16px;
	`};
`

const StyledH2 = styled(H2)`
  margin-block: 0 10px;
  ${validFor.mobile` 
    font-size: 20px;
    margin-block: 10px
  `};
`

const StyledBody = styled(Body)`
  text-align: center;
  line-height: 1.3;
`

const StyledForm = styled.form`
  width: 100%;
  margin-top: 30px;
`

const StyledGrid = styled(Box)`
  display: grid;
  grid-template-columns: 70% 30%;
  ${validFor.mobile`
    grid-template-columns: 100%;
  `}
`

const StyledFormInput = styled(FormInput)`
  height: 60px;
  font-size: 16px;
`

const StyledButton = styled.button`
  background-color: ${({ theme }) => theme.colors.onBackground};
  color: ${({ theme }) => theme.colors.background};
  border: none;
  height: 60px;
  text-transform: uppercase;
  font-size: 16px;
  cursor: pointer;
  letter-spacing: 0.5px;
  &:hover {
    background-color: ${(props) =>
      transparentize(0.1, props.theme.colors.onBackground)};
  }
`
